import { type FC, useCallback } from "react";

import { Button, Modal, Section } from "@snapchat/snap-design-system";

import { CopyToClipboardButton, SkeletonContainer } from "../../camkit/common/components";
import { GrayLabel } from "../../hosted-lenses/components/HostedLensSection.styles";
import { Flexbox, NewTag } from "../../shared/components";
import { useToggleState } from "../../shared/hooks";
import { useLensCloudApp, useOnboardLensToLensCloud } from "../hooks";

import { ModalBody, bodyCss, lensCloudHeaderCss } from "./LensCloudSection.styles";
import { Confirm, NewAppText, NoAppText, Onboard, SectionTitle } from "./messages.generated";

interface LensCloudSectionProps {
    lensId: string;
}

export const LensCloudSection: FC<LensCloudSectionProps> = ({ lensId }) => {
    const { appId, loading: appLoading } = useLensCloudApp({ lensId });
    const { onboardLens, loading: onboardLoading } = useOnboardLensToLensCloud({ lensId });
    const [{ visible, onCancel }, showModal] = useToggleState();

    const loading = appLoading || onboardLoading;
    const hasExistingApp = !loading && appId !== "";

    const onboardHandler = useCallback(async () => {
        await onboardLens();
        onCancel();
    }, [onboardLens, onCancel]);

    return (
        <Section
            title={
                <span className={lensCloudHeaderCss}>
                    <SectionTitle />
                    {/* TODO: Add start date and visibility duration days when release to GA */}
                    <NewTag />
                </span>
            }
            rightHeaderContent={
                hasExistingApp || loading ? null : (
                    <Button onClick={showModal}>
                        <Onboard />
                    </Button>
                )
            }
        >
            {loading ? (
                <SkeletonContainer loading />
            ) : (
                <>
                    <div className={bodyCss}>
                        {hasExistingApp ? (
                            <>
                                <GrayLabel>AppId</GrayLabel>
                                <Flexbox>
                                    {appId}
                                    <CopyToClipboardButton
                                        tooltip={`Copy ${appId}`}
                                        copyValue={appId}
                                        notification={{ title: `${appId} copied to clipboard` }}
                                    />
                                </Flexbox>
                            </>
                        ) : (
                            <NoAppText />
                        )}
                    </div>
                    <Modal
                        isActionButtonPending={onboardLoading}
                        visible={visible}
                        onCancel={onCancel}
                        actionText={<Confirm />}
                        onAction={onboardHandler}
                    >
                        <ModalBody>{<NewAppText />}</ModalBody>
                    </Modal>
                </>
            )}
        </Section>
    );
};
